import type { MediaHouseMedia } from "@/generated/graphql";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { Fragment, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiEdit, FiFolder, FiInfo, FiMoreVertical, FiTrash2 } from "react-icons/fi";

const MenuPositioner = ({
  open,
  buttonRef,
  setMenuPosition,
}: {
  open: boolean;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  setMenuPosition: (pos: { top: number; left: number }) => void;
}) => {
  useLayoutEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 6, left: rect.left });
    }
  }, [open, buttonRef, setMenuPosition]);
  return null;
};

interface ThreeDotMenuProps {
  media: MediaHouseMedia;
  onProperties: () => void;
  onMove: () => void;
  onDelete: () => void;
}

export function ThreeDotMenu({ media: _media, onProperties, onMove, onDelete }: ThreeDotMenuProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  return (
    <Menu as="div" className="relative inline-block">
      {({ open }) => (
        <>
          <MenuPositioner open={open} buttonRef={buttonRef} setMenuPosition={setMenuPosition} />

          <MenuButton
            ref={buttonRef}
            className="flex items-center justify-center w-6 h-6 rounded-lg
              text-white/80 bg-black/30 backdrop-blur-sm
              hover:bg-black/50 hover:text-white
              transition-all duration-100"
          >
            <FiMoreVertical className="h-3.5 w-3.5" />
          </MenuButton>

          {open &&
            createPortal(
              <Transition
                show={open}
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95 translate-y-1"
                enterTo="transform opacity-100 scale-100 translate-y-0"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems
                  static
                  className="w-44 rounded-xl overflow-hidden focus:outline-none"
                  style={{
                    position: "fixed",
                    top: `${menuPosition.top}px`,
                    left: `${menuPosition.left}px`,
                    zIndex: 9999,
                    background: "rgba(255,255,255,0.96)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    boxShadow:
                      "0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px rgba(226,232,240,0.8)",
                  }}
                >
                  {/* Gradient top accent */}
                  <div
                    className="h-0.5 w-full"
                    style={{ background: "linear-gradient(90deg, #6366f1, #14b8a6)" }}
                  />

                  <div className="py-1">
                    <MenuItem>
                      {({ focus }) => (
                        <button
                          type="button"
                          onClick={onProperties}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 transition-colors ${
                            focus ? "bg-slate-50" : ""
                          }`}
                        >
                          <span className="p-1 rounded-md bg-secondary-100 text-secondary-600 flex-shrink-0">
                            <FiInfo className="h-3 w-3" />
                          </span>
                          <span className="font-medium">Properties</span>
                        </button>
                      )}
                    </MenuItem>

                    <MenuItem>
                      {({ focus }) => (
                        <button
                          type="button"
                          onClick={onMove}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 transition-colors ${
                            focus ? "bg-slate-50" : ""
                          }`}
                        >
                          <span className="p-1 rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                            <FiFolder className="h-3 w-3" />
                          </span>
                          <span className="font-medium">Move to folder</span>
                        </button>
                      )}
                    </MenuItem>

                    <div className="mx-3 h-px bg-slate-100 my-1" />

                    <MenuItem>
                      {({ focus }) => (
                        <button
                          type="button"
                          onClick={onDelete}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs text-error-600 transition-colors ${
                            focus ? "bg-error-50" : ""
                          }`}
                        >
                          <span className="p-1 rounded-md bg-error-100 text-error-600 flex-shrink-0">
                            <FiTrash2 className="h-3 w-3" />
                          </span>
                          <span className="font-medium">Move to trash</span>
                        </button>
                      )}
                    </MenuItem>
                  </div>
                </MenuItems>
              </Transition>,
              document.body
            )}
        </>
      )}
    </Menu>
  );
}
